import moment from "moment";
import qs from "qs";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { ContractGroupListModel, ContractGroupParam, ContractGroupStatus, CustomFieldType } from "src/api/contractGroup/contractgroup-type";
import { useApiOperation } from "src/api/hooks";
import { getOrgList } from "src/api/organization/org-api";
import { MbOrg } from "src/api/organization/org-types";
import { Order, PageMeta } from "src/api/public-types";
import { BaseButton, BaseDatePicker, BaseInput, BaseInputWithSearch, BaseSelect, BaseTable } from "src/components";
import { MultiSelectFilter } from "src/components/multiSelect";
import RangeDatepicker from "src/components/RangeDatepicker";
import SearchResetButton from "src/components/SearchResetButton";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import useNavigate from "src/hooks/usePartnerNavigate";
import SearchOrgPopup from "src/pages/commonPopup/SearchOrgPopup";
import { PagePath } from "src/pages/product/details";
import { productTypeOptions, ProductTypeT } from "src/pages/product/product-types";
import ExcelDownloadButton from "src/pages/visitor/list/ExcelDownloadButton";
import { qsParse, YmdFormat } from "src/utils";
import { CONTRACT_GROUP_STATUS_OPTIONS } from "../contractgroup-constant";
import { useContractGroup } from "../hooks/useContractGroup";
import { useContractGroupColumns } from "./columns/useContractGroupColumns";

type SearchQueryParams = ContractGroupParam & {
  searchType?: string;
  searchKeyword?: string;
};

const ContractGroupList = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams: SearchQueryParams = useMemo(() => {
    // qsParse는 decoder로 decodeURIComponent를 써서 타임존 오프셋의 '+'(예: searchStartAt=...+09:00)를
    // 공백으로 바꾸지 않고 보존한다(기본 qs.parse는 '+'를 공백으로 디코딩 → Invalid Date 발생).
    const query: SearchQueryParams = qsParse(location.search) as SearchQueryParams;
    if (!query.page) {
      query.page = 0;
    }
    if (!query.size) {
      query.size = 20;
    }

    if (!query?.sort) {
      query.sort = {
        orders: [{ property: "groupId", direction: "DESC" }],
      };
    }
    return query;
  }, [location]);

  const { isAuthority } = usePartnerAuthority();

  const [contractGroupList, setContractGroupList] = useState<ContractGroupListModel[]>([]);
  const [pageMeta, setPageMeta] = useState<PageMeta>();
  const [searchParams, setSearchParams] = useState<SearchQueryParams>({ ...queryParams });
  const [customFieldSearchParams, setCustomFieldSearchParams] = useState<{
    inputValue?: string;
    optionIdList?: string;
    fieldId?: number;
  }>({
    inputValue: queryParams.inputValue,
    optionIdList: queryParams.optionIdList,
    fieldId: queryParams.fieldId,
  });

  const [isOrgOpen, setIsOrgOpen] = useState(false);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);

  const { contractGroupColumns, customFields } = useContractGroupColumns();
  const { getContractGroupList, onDownloadContractGroupExcel } = useContractGroup();
  const { executeAsync: getOrgListApi } = useApiOperation(getOrgList);

  const [customFieldOption, setCustomFieldOption] = useState<{ value: number; label: string; fieldType: CustomFieldType } | null>(null);

  // 커스텀 필드 검색 옵션
  const customFieldSearchOptions = useMemo(() => {
    return [
      { value: 0, label: "전체", fieldType: "" as CustomFieldType },
      ...customFields
        .filter((field) => field.isVisible)
        .map((field) => ({
          value: field.fieldId,
          label: field.fieldName!,
          fieldType: field.fieldType!,
        })),
    ];
  }, [customFields]);
  // 선택된 커스텀 필드의 fieldType을 메모이제이션
  const selectedCustomFieldType = useMemo(() => {
    return customFieldSearchOptions.find((option) => String(option.value) === String(customFieldSearchParams.fieldId))?.fieldType;
  }, [customFieldSearchOptions, customFieldSearchParams.fieldId]);

  // 검색 타입에 따른 파라미터 매핑 (IotConfigList 방식)
  const searchTypeAndKeyword = useMemo(() => {
    const searchData: ContractGroupParam = {};
    switch (searchParams.searchType) {
      case "groupName": // 계약그룹명
        searchData.groupName = searchParams.searchKeyword;
        break;
      case "GROUP_ID": // 계약그룹 ID
        searchData.searchType = "GROUP_ID";
        searchData.searchValue = searchParams.searchKeyword;
        break;
      default: // 전체 또는 빈값
        searchData.searchValue = searchParams.searchKeyword;
        break;
    }
    return searchData;
  }, [searchParams.searchKeyword, searchParams.searchType]);

  // URL 파라미터와 상태 동기화
  useEffect(() => {
    // searchValue/groupName에서 searchKeyword/searchType 복원
    let restoredSearchParams = { ...queryParams };

    if (queryParams.groupName) {
      restoredSearchParams.searchKeyword = queryParams.groupName;
      restoredSearchParams.searchType = "groupName";
    } else if (queryParams.searchType === "GROUP_ID" && queryParams.searchValue) {
      restoredSearchParams.searchKeyword = queryParams.searchValue;
      restoredSearchParams.searchType = "GROUP_ID";
    } else if (queryParams.searchValue) {
      restoredSearchParams.searchKeyword = queryParams.searchValue;
      restoredSearchParams.searchType = "";
    }
    setSearchParams(restoredSearchParams);

    // 이용기간 검색(searchStartAt/searchEndAt) 복원
    setDateRange([
      queryParams.searchStartAt ? moment(queryParams.searchStartAt).toDate() : null,
      queryParams.searchEndAt ? moment(queryParams.searchEndAt).toDate() : null,
    ]);
    setCustomFieldSearchParams({
      inputValue: queryParams.inputValue,
      optionIdList: queryParams.optionIdList,
      fieldId: queryParams.fieldId,
    });
  }, [queryParams, location]);

  // customFieldOption 복원 (customFields가 로드된 후)
  useEffect(() => {
    if (queryParams.fieldId && customFields.length > 0) {
      const foundOption = customFieldSearchOptions.find((option) => String(option.value) === String(queryParams.fieldId));

      setCustomFieldOption(foundOption || null);
    } else {
      setCustomFieldOption(null);
    }
  }, [queryParams.fieldId, customFields, customFieldSearchOptions]);

  // API 호출 (IotConfig와 동일하게 queryParams 기반)
  useEffect(() => {
    const fetchContractGroupList = async () => {
      // API 요청용 파라미터 (mbOrganizationName 제외)
      const { mbOrganizationName, ...apiParams } = queryParams;
      const { data } = await getContractGroupList(apiParams);
      const contractGroups = data.data.content;

      // 1. 각 계약그룹의 mbOrganizationIdList에서 첫 번째(index 0) ID만 수집
      const firstOrgIds = [
        ...new Set(
          contractGroups?.map((item) => item.mbOrganizationIdList?.[0]).filter((id) => id !== undefined && id !== null), // undefined와 null 제거 (0 은 getOrgList 에서 걸러짐)
        ),
      ];

      if (firstOrgIds.length > 0) {
        // 2. 첫 번째 조직들의 정보 조회
        const orgList = await getOrgListApi({
          page: 0,
          size: firstOrgIds.length,
          id: firstOrgIds.join(","),
        })
          .then(({ data: orgData }) => orgData.data.content)
          .catch(() => []);
        const orgMap = new Map(orgList.map((org) => [org.id, org]));

        // 3. result에 조직 정보 추가
        const updatedResult = contractGroups?.map((item) => {
          const orgIdList = item.mbOrganizationIdList || [];

          // 조직 ID 리스트가 빈 배열이면 "-" 표시
          if (orgIdList.length === 0) {
            return {
              ...item,
              orgName: "-",
              orgDisplayName: "-",
            };
          }

          const firstOrgId = orgIdList[0];
          const org = orgMap.get(firstOrgId);

          // 중복 제거한 실제 조직 수 계산
          const uniqueOrgIds = [...new Set(orgIdList)];
          const remainingCount = uniqueOrgIds.length - 1; // 첫 번째 조직 제외한 나머지

          return {
            ...item,
            orgName: org?.classifications?.includes("INDIVIDUAL") ? org?.ceoName : org?.name || "미지정",
            orgDisplayName:
              remainingCount > 0 ? `${org ? org.name || org.ceoName : "미지정"} 외 ${remainingCount}개` : org ? org.name || org.ceoName : "미지정",
          };
        });

        setContractGroupList(updatedResult || []);
      } else {
        setContractGroupList(contractGroups);
      }

      setPageMeta(data.meta.pageMeta);
    };
    fetchContractGroupList();
  }, [queryParams]);

  const navigateWithQueryParams = (passParams?: SearchQueryParams, includeSearch = false) => {
    // 허용된 파라미터만 추출
    const allowedParams = {
      page: queryParams.page,
      size: queryParams.size,
      searchValue: queryParams.searchValue,
      groupName: queryParams.groupName,
      searchType: queryParams.searchType,
      mbOrganizationId: queryParams.mbOrganizationId,
      mbOrganizationName: queryParams.mbOrganizationName,
      groupStatus: queryParams.groupStatus,
      spaceProductType: queryParams.spaceProductType,
      searchStartAt: queryParams.searchStartAt,
      searchEndAt: queryParams.searchEndAt,
      fieldId: queryParams.fieldId,
      inputValue: queryParams.inputValue,
      optionIdList: queryParams.optionIdList,
    };

    // 검색 실행 시 searchTypeAndKeyword 추가
    const searchParams = includeSearch ? searchTypeAndKeyword : {};

    // searchValue와 groupName이 동시에 있으면 기존 것 제거
    if (searchParams?.searchValue && allowedParams.groupName) {
      allowedParams.groupName = undefined;
    }
    if (searchParams?.groupName && allowedParams.searchValue) {
      allowedParams.searchValue = undefined;
    }
    // 검색 실행 시 GROUP_ID가 아니면 기존 searchType 제거 (전체/계약그룹명 검색)
    if (includeSearch && searchParams?.searchType !== "GROUP_ID") {
      allowedParams.searchType = undefined;
    }

    // inputValue와 optionIdList 상호배타적 조건은 각 검색 시점에서 처리됨

    const newQueryParams = { ...allowedParams, ...searchParams, ...(passParams || {}) };

    // undefined 값 제거
    const cleanParams = Object.fromEntries(Object.entries(newQueryParams).filter(([_, value]) => value !== undefined && value !== ""));

    navigate(location.pathname + "?" + decodeURIComponent(qs.stringify(cleanParams, { allowDots: true })));
  };

  // 이용기간 검색 (계약그룹에 속한 개별 계약 기간과 오버랩되면 조회 - 판단은 백엔드)
  // 공용공간 예약 목록(ReservationList)의 기간선택과 동일 방식, searchStartAt/searchEndAt 파라미터명만 다름
  const handleOnDateRangeChange = (range: [Date | null, Date | null]) => {
    const [startDate, endDate] = range;
    setDateRange(range);

    // 날짜 선택값 없이 검색시 날짜 초기화
    if (range[0] === null && range[1] === null) {
      navigateWithQueryParams({ ...queryParams, searchStartAt: undefined, searchEndAt: undefined, page: 0 });
      return;
    }

    const searchStartAt = moment(startDate).startOf("days").format(YmdFormat.WITH_TIME_ZONE);
    const searchEndAt = moment(endDate).endOf("days").format(YmdFormat.WITH_TIME_ZONE);

    if (startDate && endDate) {
      navigateWithQueryParams({ ...queryParams, searchStartAt: String(searchStartAt), searchEndAt: String(searchEndAt), page: 0 });
    }
  };

  return (
    <>
      <div className="page-product-list">
        <div className="contents-container__table">
          <div className="contents-container__search-wrap">
            <div className="left-area">
              <section>
                <div className="left-area__index">
                  <span>지정검색</span>
                </div>
                <div className="left-area__contents">
                  <div className="minmax160">
                    <BaseInput
                      type="text"
                      placeholder="계약자 선택"
                      readonly={true}
                      value={searchParams.mbOrganizationName || ""}
                      onClick={(e: any) => {
                        e.preventDefault();
                        setIsOrgOpen(true);
                      }}
                      onClearClick={() => {
                        setSearchParams({ ...searchParams, mbOrganizationName: undefined });
                        navigateWithQueryParams({
                          ...queryParams,
                          mbOrganizationId: undefined,
                          mbOrganizationName: undefined,
                          page: 0,
                        });
                      }}
                    />
                    <SearchOrgPopup
                      isOpen={isOrgOpen}
                      onClick={(selected: MbOrg) => {
                        const orgName = selected.name || selected.ceoName;
                        navigateWithQueryParams(
                          {
                            ...queryParams,
                            mbOrganizationId: selected.id,
                            mbOrganizationName: orgName,
                            page: 0,
                          },
                          true,
                        );
                        setSearchParams({ ...searchParams, mbOrganizationName: orgName });
                        setIsOrgOpen(false);
                      }}
                      onClose={() => setIsOrgOpen(false)}
                      defaultChecked={searchParams.mbOrganizationId ? [String(searchParams.mbOrganizationId)] : []}
                    />
                  </div>
                </div>
              </section>

              <section>
                <div className="left-area__index">
                  <span>조건검색</span>
                </div>
                <div className="left-area__contents">
                  <div className="flex-center">
                    <BaseInputWithSearch
                      type="text"
                      className="mr10"
                      selectValue={searchParams?.searchType}
                      inputValue={searchParams?.searchKeyword}
                      stateOptions={[
                        { value: "", label: "전체" },
                        { value: "GROUP_ID", label: "계약그룹 ID" },
                        { value: "groupName", label: "계약그룹명" },
                      ]}
                      setStateValue={(searchType: string) => {
                        setSearchParams({ ...searchParams, searchType });
                      }}
                      onChange={(searchKeyword: string) => {
                        setSearchParams({ ...searchParams, searchKeyword });
                      }}
                      onSearchClick={() => {
                        navigateWithQueryParams({ page: 0 }, true);
                      }}
                      onKeyUp={() => {
                        navigateWithQueryParams({ page: 0 }, true);
                      }}
                      onClearClick={() => {
                        navigateWithQueryParams({
                          groupName: undefined,
                          searchType: undefined,
                          searchKeyword: undefined,
                          searchValue: undefined,
                          page: 0,
                        });
                        setSearchParams({ ...searchParams, searchType: undefined, searchKeyword: undefined });
                      }}
                    />
                    <div className="minmax160">
                      <BaseSelect
                        className="w-100"
                        placeholder="상태"
                        stateOptions={CONTRACT_GROUP_STATUS_OPTIONS}
                        value={queryParams.groupStatus || ""}
                        setStateValue={(value: string) => {
                          navigateWithQueryParams(
                            {
                              ...queryParams,
                              groupStatus: value === "" ? undefined : (value as ContractGroupStatus),
                              page: 0,
                            },
                            true,
                          );
                        }}
                      />
                    </div>
                    <div className="minmax160 ml10">
                      <BaseSelect
                        value={queryParams.spaceProductType || ""}
                        stateOptions={productTypeOptions}
                        setStateValue={(value: string) => {
                          navigateWithQueryParams(
                            { ...queryParams, spaceProductType: value === "" ? undefined : (value as ProductTypeT), page: 0 },
                            true,
                          );
                        }}
                        placeholder="상품 유형"
                      />
                    </div>
                    {/* 이용기간 검색: 이용 시작일 ~ 이용 종료일 */}
                    <div className="minmax240 ml10">
                      <RangeDatepicker
                        dateRange={dateRange}
                        onChange={handleOnDateRangeChange}
                        onClearClick={() => {
                          setDateRange([null, null]);
                          navigateWithQueryParams({ ...queryParams, searchStartAt: undefined, searchEndAt: undefined, page: 0 });
                        }}
                      />
                    </div>
                  </div>

                  <div className="flex-center ">
                    <div className="search-divider mr10"></div>
                    <div className="left-area__index pt0">
                      <span className="minmax80 ">커스텀 필드 검색</span>
                    </div>
                    {/* 커스텀 필드 선택영역 > 커스텀 필드 선택 시 커스텀 필드 검색영역 활성화 */}
                    <BaseSelect
                      className="minmax240"
                      value={customFieldSearchOptions.find((item) => String(item.value) === String(customFieldSearchParams.fieldId))?.value || ""}
                      stateOptions={customFieldSearchOptions}
                      setStateValue={(value: string) => {
                        if (Number(value) === 0) {
                          setCustomFieldOption(null);
                          setCustomFieldSearchParams({
                            fieldId: undefined,
                            inputValue: undefined,
                            optionIdList: undefined,
                          });
                          navigateWithQueryParams({
                            ...queryParams,
                            fieldId: undefined,
                            inputValue: undefined,
                            optionIdList: undefined,
                            page: 0,
                          });
                        }

                        const foundOption = customFieldSearchOptions?.find((option) => String(option.value) === String(value));
                        setCustomFieldOption(foundOption || null);
                        setCustomFieldSearchParams({
                          fieldId: foundOption?.value,
                          inputValue: undefined,
                          optionIdList: undefined,
                        });
                      }}
                      placeholder="커스텀 필드"
                    />

                    {/* 커스텀 필드 검색영역 */}
                    {(selectedCustomFieldType === "TEXT" || selectedCustomFieldType === "NUMBER" || selectedCustomFieldType === "URL") && (
                      <BaseInput
                        type="text"
                        className="ml10 minmax200"
                        value={customFieldSearchParams.inputValue || ""}
                        placeholder="검색어를 입력하세요"
                        onChange={(value: string) => {
                          setCustomFieldSearchParams({ ...customFieldSearchParams, inputValue: value });
                        }}
                        onSearchClick={() => {
                          navigateWithQueryParams(
                            {
                              ...customFieldSearchParams,
                              optionIdList: undefined, // inputValue 검색 시 optionIdList 제거
                              page: 0,
                            },
                            true,
                          );
                        }}
                        onKeyUp={() => {
                          navigateWithQueryParams(
                            {
                              ...customFieldSearchParams,
                              optionIdList: undefined, // inputValue 검색 시 optionIdList 제거
                              page: 0,
                            },
                            true,
                          );
                        }}
                      />
                    )}
                    {selectedCustomFieldType === "CALENDAR" && (
                      <BaseDatePicker
                        className="ml10 minmax200"
                        type="date"
                        selectedDate={customFieldSearchParams.inputValue ? moment(customFieldSearchParams.inputValue).toDate() : undefined}
                        setDate={(date: any) => {
                          setCustomFieldSearchParams({ ...customFieldSearchParams, inputValue: date });
                          navigateWithQueryParams(
                            {
                              ...customFieldSearchParams,
                              inputValue: date,
                              optionIdList: undefined, // inputValue 검색 시 optionIdList 제거
                              page: 0,
                            },
                            true,
                          );
                        }}
                      />
                    )}

                    {(selectedCustomFieldType === "CHECKBOX" || selectedCustomFieldType === "RADIO") && (
                      <div className="ml10 minmax200">
                        <MultiSelectFilter
                          className="ml10 "
                          options={
                            customFields
                              .find((item) => String(item.fieldId) === String(customFieldOption?.value))
                              ?.optionList?.map((option) => ({ value: String(option.optionId), label: option.optionName })) || []
                          }
                          value={(customFieldSearchParams.optionIdList?.split(",").map(String) as string[]) || []}
                          onChange={(values) => {
                            const optionIdListValue = values.join(",");
                            setCustomFieldSearchParams({ ...customFieldSearchParams, optionIdList: optionIdListValue });
                            navigateWithQueryParams(
                              {
                                ...customFieldSearchParams,
                                optionIdList: optionIdListValue,
                                inputValue: undefined, // optionIdList 검색 시 inputValue 제거
                                page: 0,
                              },
                              true,
                            );
                          }}
                          placeholder="선택해주세요"
                        />
                      </div>
                    )}
                  </div>
                  {/* 검색해제 버튼 */}
                  <SearchResetButton />
                </div>
              </section>
            </div>
            <div className="right-area flex-direction-column flex-start-start">
              {isAuthority("w") && (
                <div className="flex-center">
                  <BaseButton className="color-white" title="커스텀필드 관리" onClick={() => navigate(PagePath.contractGroup.customField)} />
                  <BaseButton className="ml10" title="+ 계약그룹 등록" onClick={() => navigate(PagePath.contractGroup.form)} />
                </div>
              )}
            </div>
          </div>
          <BaseTable
            data={contractGroupList}
            currentSize={Number(queryParams.size) ?? 20}
            sizeOption={(sizeValue) => {
              navigateWithQueryParams({ ...queryParams, size: sizeValue, page: 0 });
            }}
            pageIndex={Number(queryParams.page) || 0}
            totalElements={pageMeta?.totalElements}
            totalPages={pageMeta?.totalPages || 1}
            goPage={(page: number) => navigateWithQueryParams({ page })}
            orders={[]}
            setOrders={(orders?: Array<Order>) => {}}
            columns={contractGroupColumns}
            disabledSortHeaders={contractGroupColumns.map((column) => column.accessor as string)}
            children={
              <ExcelDownloadButton
                onClick={() => {
                  onDownloadContractGroupExcel(contractGroupColumns, queryParams);
                }}
              >
                엑셀받기
              </ExcelDownloadButton>
            }
          />
        </div>
      </div>
    </>
  );
};

export default ContractGroupList;
